import React, { useMemo, useState } from "react";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import moment from "moment";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { nestedSurfaceSx, SERIES_TONES, toneChipSx, toneMain } from "./channelTheme";
import { windowPoints } from "./channelUtils";

export const RANGES = [
  { key: "1h", label: "1h", hours: 1 },
  { key: "24h", label: "24h", hours: 24 },
  { key: "3d", label: "3d", hours: 72 },
  { key: "7d", label: "7d", hours: 168 },
];
export const MAX_COMPARE = 4;

const toXY = (points) => points.map((p) => ({ x: p.t, y: p.v }));

export default function ChannelHistoryChart({
  channel,
  channels = [],
  range = "24h",
  onRangeChange,
  compareKeys = [],
  onCompareChange,
  height = 280,
}) {
  const theme = useTheme();
  const { palette, functions: { rgba } } = theme;
  const hours = (RANGES.find((r) => r.key === range) || RANGES[0]).hours;
  const [historyEnd, setHistoryEnd] = useState(null);
  const end = historyEnd ?? Date.now();
  const earliest = channel?.points?.length
    ? Math.min(...channel.points.map((point) => point.t))
    : end;
  const from = end - hours * 3600e3;

  const { data, options, hasData } = useMemo(() => {
    const now = end;
    const from = now - hours * 3600e3;
    const textColor = palette.text.main;
    const gridColor = rgba(palette.text.main, 0.08);
    const primary = windowPoints(channel?.points || [], hours, now);
    const compares = compareKeys
      .map((k) => channels.find((c) => c.key === k))
      .filter(Boolean);
    const needsY1 = compares.some((c) => c.unit !== channel?.unit);

    const datasets = [
      {
        label: `${channel?.name} (${channel?.unit || "value"})`,
        data: toXY(primary),
        yAxisID: "y",
        borderColor: toneMain(palette, "info"),
        backgroundColor: rgba(toneMain(palette, "info"), 0.12),
        fill: false,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
        tension: 0,
      },
      ...compares.map((c, i) => {
        const tone = SERIES_TONES[(i + 1) % SERIES_TONES.length];
        return {
          label: `${c.name} (${c.unit || "value"})`,
          data: toXY(windowPoints(c.points, hours, now)),
          yAxisID: c.unit === channel?.unit ? "y" : "y1",
          borderColor: toneMain(palette, tone),
          backgroundColor: "transparent",
          borderWidth: 1.5,
          pointRadius: 2,
          pointHoverRadius: 5,
          tension: 0,
        };
      }),
      ...(channel?.thresholds || [])
        .filter((t) => t.subject === "value")
        .map((t) => ({
          label: `${t.label} ${t.operator} ${t.value}`,
          isThreshold: true,
          data: [
            { x: from, y: t.value },
            { x: now, y: t.value },
          ],
          yAxisID: "y",
          borderColor: toneMain(palette, t.severity === "alarm" ? "error" : "warning"),
          borderDash: [6, 4],
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
        })),
    ];

    const tickFormat = hours <= 24 ? "h:mm A" : "MMM D";
    return {
      hasData: primary.length > 0,
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        interaction: { mode: "nearest", axis: "x", intersect: false },
        plugins: {
          legend: {
            display: compares.length > 0,
            labels: {
              color: textColor,
              boxWidth: 12,
              filter: (item, chartData) => !chartData.datasets[item.datasetIndex]?.isThreshold,
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => (items[0] ? moment(items[0].parsed.x).format("MMM D, YYYY h:mm:ss A") : ""),
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            min: from,
            max: now,
            ticks: { color: textColor, maxTicksLimit: 7, callback: (v) => moment(v).format(tickFormat) },
            grid: { color: gridColor },
          },
          y: {
            position: "left",
            suggestedMin: channel?.kind === "level" ? 0 : undefined,
            suggestedMax: channel?.kind === "level" ? 100 : undefined,
            ticks: { color: textColor },
            grid: { color: gridColor },
            title: { display: !!channel?.unit, text: channel?.unit, color: textColor },
          },
          y1: {
            display: needsY1,
            position: "right",
            ticks: { color: textColor },
            grid: { drawOnChartArea: false },
          },
        },
      },
    };
  }, [channel, channels, compareKeys, hours, end, palette, rgba]);

  const compareOptions = channels.filter((c) => c.key !== channel?.key && c.points.length > 0);
  const toggleCompare = (key) => {
    if (compareKeys.includes(key)) onCompareChange?.(compareKeys.filter((k) => k !== key));
    else if (compareKeys.length < MAX_COMPARE) onCompareChange?.([...compareKeys, key]);
  };

  return (
    <MDBox sx={(t) => ({ ...nestedSurfaceSx(t), p: 1.5, height: "100%" })}>
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
        <MDTypography variant="button" fontWeight="bold">
          History
        </MDTypography>
        <MDBox display="flex" gap={0.5}>
          {RANGES.map((r) => (
            <MDButton
              key={r.key}
              size="small"
              color="info"
              variant={range === r.key ? "gradient" : "outlined"}
              onClick={() => onRangeChange?.(r.key)}
              aria-pressed={range === r.key}
              sx={{ minWidth: 44, minHeight: 28, py: 0.25, px: 1 }}
            >
              {r.label}
            </MDButton>
          ))}
        </MDBox>
      </MDBox>

      <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
        <MDButton
          size="small"
          color="info"
          variant="outlined"
          disabled={from <= earliest}
          onClick={() => setHistoryEnd(Math.max(earliest + hours * 3600e3, end - hours * 3600e3))}
        >
          Earlier
        </MDButton>
        <MDButton
          size="small"
          color="info"
          variant="outlined"
          disabled={historyEnd === null}
          onClick={() => {
            const next = end + hours * 3600e3;
            setHistoryEnd(next >= Date.now() ? null : next);
          }}
        >
          Later
        </MDButton>
        <MDButton size="small" color="info" variant="outlined" onClick={() => setHistoryEnd(null)}>
          Latest
        </MDButton>
        <MDTypography variant="caption" color="text" aria-live="polite">
          {moment(from).format("MMM D, YYYY h:mm A")} – {moment(end).format("MMM D, YYYY h:mm A")}
        </MDTypography>
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        {data.datasets[0].data.length} recorded samples in this window.
        {" "}Hover or tap a point for its timestamp and value. Browsing loaded history only.
      </MDTypography>
      <MDBox height={height} position="relative">
        {hasData ? (
          <Line data={data} options={options} />
        ) : (
          <MDBox height="100%" display="flex" alignItems="center" justifyContent="center">
            <MDTypography variant="button" color="text">
              No recorded readings in this time window.
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {compareOptions.length > 0 && (
        <MDBox mt={1.5}>
          <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.75}>
            Compare with (max {MAX_COMPARE})
          </MDTypography>
          <MDBox display="flex" gap={0.75} flexWrap="wrap">
            {compareOptions.map((c) => {
              const idx = compareKeys.indexOf(c.key);
              const active = idx >= 0;
              const tone = active ? SERIES_TONES[(idx + 1) % SERIES_TONES.length] : "secondary";
              return (
                <Chip
                  key={c.key}
                  size="small"
                  clickable
                  label={c.name}
                  onClick={() => toggleCompare(c.key)}
                  disabled={!active && compareKeys.length >= MAX_COMPARE}
                  aria-pressed={active}
                  sx={(t) => ({ ...toneChipSx(tone)(t), ...(active ? {} : { opacity: 0.85 }) })}
                />
              );
            })}
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}
